import React, { useContext, useEffect, useState } from 'react';
import './Landing.css';

// new imports for generated SVG assets
import heroSvg from '../assets/hero-spirits.svg';
import cheersSvg from '../assets/cheers.svg';
import beerSvg from '../assets/beer.svg';
import whiskeySvg from '../assets/whiskey.svg';
import wineSvg from '../assets/wine.svg';
import beerPhoto from '../assets/beer.jpg'; // add
import winePhoto from '../assets/vin.jpg';   // add
import whiskyPhoto from '../assets/whisky.jpg'; // add

import { LocaleContext } from '../locale';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

export default function Landing() {
  const { t, locale } = useContext(LocaleContext);
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [featured, setFeatured] = useState({ bestWine: null, mostExpensive: null, newestPlus: null });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    // load products to compute featured
    const loadFeatured = async () => {
      try {
        const res = await fetch(`${API_BASE}/products`);
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data.products) ? data.products : [];
        if (items.length === 0) return;

        const byPriceDesc = [...items].sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        const wines = items.filter(p => p.category === 'wine').sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        const bestWine = wines[0] || null; // Meilleure vente -> most priced wine
        const mostExpensive = byPriceDesc[0] || null; // Édition limitée -> most priced overall

        // newest by created_at, fallback to most recent index order
        const byNewest = [...items].sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime();
          const db = new Date(b.created_at || 0).getTime();
          return db - da;
        });
        const newestRaw = byNewest[0] || null;
        const newestPlus = newestRaw ? { ...newestRaw, price: parseFloat(newestRaw.price) + 30 } : null; // Nouvelle arrivée -> price + 30

        setFeatured({ bestWine, mostExpensive, newestPlus });
      } catch {
        /* silent fallback to translations */
      }
    };
    loadFeatured();
  }, []);

  const formatPrice = (n) => {
    try {
      return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' }).format(n);
    } catch {
      return `€${Number(n).toFixed(2)}`;
    }
  };

  const catLabel = (c) => {
    if (c === 'beer') return t('productsPage.beer');
    if (c === 'wine') return t('productsPage.wine');
    if (c === 'whisky') return t('productsPage.whisky');
    if (c === 'snacks') return (locale === 'fr' ? 'Amuse‑gueules' : 'Snacks');
    return c;
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setEmail('');
    }
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('scroll-focus');
    setTimeout(() => el.classList.remove('scroll-focus'), 1200);
  };

  return (
    <section className="landing">
      {/* Enhanced Hero Section */}
      <div className="hero">
        <div className="hero-particles"></div>
        <div className="container hero-inner">
          <div className={`hero-left ${isVisible ? 'animate-in' : ''}`}>
            <div className="hero-badge">
              <span className="badge-text">{t('landing.badge')}</span>
            </div>
            <h1 className="h1 hero-title">
              {t('landing.heading')}
            </h1>
            <p className="hero-subtitle">
              {t('landing.sub')}
            </p>
            
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">2,500+</span>
                <span className="stat-label">{t('landing.stats.customers')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">150+</span>
                <span className="stat-label">{t('landing.stats.spirits')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">4.9★</span>
                <span className="stat-label">{t('landing.stats.rating')}</span>
              </div>
            </div>

            <div className="hero-actions">
              <a
                className="btn primary large pulse"
                href="#featured"
                onClick={(e) => { e.preventDefault(); scrollToId('featured'); }}
              >
                {/* ...existing svg... */}
                {t('landing.shopPremium')}
              </a>
              <a
                className="btn secondary large"
                href="#collections"
                onClick={(e) => { e.preventDefault(); scrollToId('collections'); }}
              >
                {/* ...existing svg... */}
                {t('landing.exploreCategories')}
              </a>
            </div>
            <div className="hero-trust">
              <span className="trust-text">{t('landing.trust.title')}</span>
              <div className="trust-badges">
                <div className="trust-badge">🚚 {t('landing.trust.freeShipping')}</div>
                <div className="trust-badge">🔒 {t('landing.trust.securePayment')}</div>
                <div className="trust-badge">↩️ {t('landing.trust.easyReturns')}</div>
              </div>
            </div>
          </div>
          
          <div className={`hero-right ${isVisible ? 'animate-in-delay' : ''}`}>
            <div className="hero-card floating">
              <div className="card-glow"></div>
              <img alt="spirits hero" src={heroSvg} />
              <div className="floating-elements">
                <div className="floating-element element-1">🍺</div>
                <div className="floating-element element-2">🍷</div>
                <div className="floating-element element-3">🥃</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Featured Products */}
      <div id="featured" className="container section featured">
        <div className="section-header">
          <h2 className="h2 section-title">{t('landing.featured')}</h2>
          <p className="section-subtitle">{t('landing.featuredSubtitle')}</p>
        </div>
        
        <div className="products-showcase">
          {/* Meilleure vente: most priced wine */}
          <div className="product-card featured-product">
            <div className="product-badge">{t('landing.badges.bestSeller')}</div>
            <div className={`product-media ${featured.bestWine?.category || 'wine'}`}>
              {featured.bestWine?.image_url ? (
                <img
                  className="featured-image"
                  src={featured.bestWine.image_url}
                  alt={featured.bestWine.name}
                  loading="lazy"
                  onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <div className="product-rating">
                <span className="stars">★★★★★</span>
                <span className="rating-text">4.9/5</span>
              </div>
            </div>
            <div className="product-body">
              <div className="product-category">
                {featured.bestWine ? catLabel(featured.bestWine.category) : t('landing.featuredCards.whiskey.category')}
              </div>
              <div className="product-title">
                {featured.bestWine ? featured.bestWine.name : t('landing.featuredCards.whiskey.title')}
              </div>
              <div className="product-description">
                {featured.bestWine
                  ? `${featured.bestWine.type} · ${featured.bestWine.marque}`
                  : t('landing.featuredCards.whiskey.desc')}
              </div>
              <div className="product-price">
                <span className="current-price">
                  {featured.bestWine ? formatPrice(parseFloat(featured.bestWine.price)) : t('landing.featuredCards.whiskey.priceCurrent')}
                </span>
                {featured.bestWine && featured.bestWine.price < (featured.mostExpensive?.price || 0) ? (
                  <span className="original-price">{formatPrice((parseFloat(featured.bestWine.price) * 1.2))}</span>
                ) : featured.bestWine ? null : (
                  <span className="original-price">{t('landing.featuredCards.whiskey.priceOld')}</span>
                )}
              </div>
              <button className="btn product-btn">{t('common.addToCart')}</button>
            </div>
          </div>

          {/* Nouvelle arrivée: latest item with price +30 */}
          <div className="product-card featured-product">
            <div className="product-badge new">{t('landing.badges.new')}</div>
            <div className={`product-media ${featured.newestPlus?.category || 'gin'}`}>
              {featured.newestPlus?.image_url ? (
                <img
                  className="featured-image"
                  src={featured.newestPlus.image_url}
                  alt={featured.newestPlus.name}
                  loading="lazy"
                  onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <div className="product-rating">
                <span className="stars">★★★★☆</span>
                <span className="rating-text">4.7/5</span>
              </div>
            </div>
            <div className="product-body">
              <div className="product-category">
                {featured.newestPlus ? catLabel(featured.newestPlus.category) : t('landing.featuredCards.gin.category')}
              </div>
              <div className="product-title">
                {featured.newestPlus ? featured.newestPlus.name : t('landing.featuredCards.gin.title')}
              </div>
              <div className="product-description">
                {featured.newestPlus
                  ? `${featured.newestPlus.type} · ${featured.newestPlus.marque}`
                  : t('landing.featuredCards.gin.desc')}
              </div>
              <div className="product-price">
                <span className="current-price">
                  {featured.newestPlus ? formatPrice(parseFloat(featured.newestPlus.price)) : t('landing.featuredCards.gin.priceCurrent')}
                </span>
              </div>
              <button className="btn product-btn">{t('common.addToCart')}</button>
            </div>
          </div>

          {/* Édition limitée: most priced overall */}
          <div className="product-card featured-product">
            <div className="product-badge limited">{t('landing.badges.limited')}</div>
            <div className={`product-media ${featured.mostExpensive?.category || 'wine'}`}>
              {featured.mostExpensive?.image_url ? (
                <img
                  className="featured-image"
                  src={featured.mostExpensive.image_url}
                  alt={featured.mostExpensive.name}
                  loading="lazy"
                  onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <div className="product-rating">
                <span className="stars">★★★★★</span>
                <span className="rating-text">4.8/5</span>
              </div>
            </div>
            <div className="product-body">
              <div className="product-category">
                {featured.mostExpensive ? catLabel(featured.mostExpensive.category) : t('landing.featuredCards.wine.category')}
              </div>
              <div className="product-title">
                {featured.mostExpensive ? featured.mostExpensive.name : t('landing.featuredCards.wine.title')}
              </div>
              <div className="product-description">
                {featured.mostExpensive
                  ? `${featured.mostExpensive.type} · ${featured.mostExpensive.marque}`
                  : t('landing.featuredCards.wine.desc')}
              </div>
              <div className="product-price">
                <span className="current-price">
                  {featured.mostExpensive ? formatPrice(parseFloat(featured.mostExpensive.price)) : t('landing.featuredCards.wine.priceCurrent')}
                </span>
              </div>
              <button className="btn product-btn">{t('common.addToCart')}</button>
            </div>
          </div>
        </div>
        
        <div className="featured-actions">
          <a href="#/products" className="btn secondary large">{t('landing.viewAllProducts')}</a>
        </div>
      </div>

      {/* New Categories Showcase - Replacing Testimonials */}
      <div id="collections" className="section categories-showcase">
        <div className="container">
          <div className="section-header">
            <h2 className="h2 section-title">{t('landing.categoriesShowcase.title')}</h2>
            <p className="section-subtitle">{t('landing.categoriesShowcase.subtitle')}</p>
          </div>
          
          <div className="category-showcase-grid">
            <div
              className="category-showcase-item beer"
              onClick={() => (window.location.hash = '#/products?cat=beer')}
              style={{cursor:'pointer'}}
            >
              <div className="category-showcase-content">
                <h3 className="category-showcase-title">{t('landing.categoriesShowcase.items.beer.title')}</h3>
                <p className="category-showcase-description">
                  {t('landing.categoriesShowcase.items.beer.desc')}
                </p>
                <a href="#/products?cat=beer" className="category-showcase-btn">
                  {t('landing.categoriesShowcase.items.beer.cta')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
              <div className="category-showcase-image">
                {/* Replace placeholder with real image, keep fallback */}
                <img
                  src={beerPhoto}
                  alt={t('landing.categoriesShowcase.items.beer.title')}
                  className="category-showcase-photo"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const ph = e.currentTarget.parentElement.querySelector('.image-placeholder');
                    if (ph) ph.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder" style={{ display: 'none' }}>
                  <span className="placeholder-icon">🍺</span>
                </div>
              </div>
            </div>
            
            <div
              className="category-showcase-item wine"
              onClick={() => (window.location.hash = '#/products?cat=wine')}
              style={{cursor:'pointer'}}
            >
              <div className="category-showcase-content">
                <h3 className="category-showcase-title">{t('landing.categoriesShowcase.items.wine.title')}</h3>
                <p className="category-showcase-description">
                  {t('landing.categoriesShowcase.items.wine.desc')}
                </p>
                <a href="#/products?cat=wine" className="category-showcase-btn">
                  {t('landing.categoriesShowcase.items.wine.cta')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
              <div className="category-showcase-image">
                {/* Use vin.jpg with fallback to placeholder */}
                <img
                  src={winePhoto}
                  alt={t('landing.categoriesShowcase.items.wine.title')}
                  className="category-showcase-photo"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const ph = e.currentTarget.parentElement.querySelector('.image-placeholder');
                    if (ph) ph.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder" style={{ display: 'none' }}>
                  <span className="placeholder-icon">🍷</span>
                </div>
              </div>
            </div>
            
            <div
              className="category-showcase-item whisky"
              onClick={() => (window.location.hash = '#/products?cat=whisky')}
              style={{cursor:'pointer'}}
            >
              <div className="category-showcase-content">
                <h3 className="category-showcase-title">{t('landing.categoriesShowcase.items.whisky.title')}</h3>
                <p className="category-showcase-description">
                  {t('landing.categoriesShowcase.items.whisky.desc')}
                </p>
                <a href="#/products?cat=whisky" className="category-showcase-btn">
                  {t('landing.categoriesShowcase.items.whisky.cta')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
              <div className="category-showcase-image">
                <img
                  src={whiskyPhoto}
                  alt={t('landing.categoriesShowcase.items.whisky.title')}
                  className="category-showcase-photo"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const ph = e.currentTarget.parentElement.querySelector('.image-placeholder');
                    if (ph) ph.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder" style={{ display: 'none' }}>
                  <span className="placeholder-icon">🥃</span>
                </div>
              </div>
            </div>
            
            <div
              className="category-showcase-item snacks"
              onClick={() => (window.location.hash = '#/products?cat=snacks')}
              style={{cursor:'pointer'}}
            >
              <div className="category-showcase-content">
                <h3 className="category-showcase-title">{t('landing.categoriesShowcase.items.snacks.title')}</h3>
                <p className="category-showcase-description">
                  {t('landing.categoriesShowcase.items.snacks.desc')}
                </p>
                <a href="#/products?cat=snacks" className="category-showcase-btn">
                  {t('landing.categoriesShowcase.items.snacks.cta')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
              <div className="category-showcase-image">
                <div className="image-placeholder">
                  <span className="placeholder-icon">🧀</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="section newsletter">
        <div className="container">
          <div className="newsletter-content">
            <div className="newsletter-text">
              <h2 className="newsletter-title">{t('landing.newsletter.title')}</h2>
              <p className="newsletter-subtitle">
                {t('landing.newsletter.subtitle')}
              </p>
            </div>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <div className="input-group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('landing.newsletter.placeholder')}
                  className="newsletter-input"
                  required
                />
                <button type="submit" className="btn newsletter-btn">
                  {t('landing.newsletter.cta')}
                </button>
              </div>
              <p className="newsletter-privacy">
                {t('landing.newsletter.privacyPrefix')} <a href="#privacy">{t('landing.newsletter.privacyLink')}</a>.
              </p>
            </form>
          </div>
        </div>
      </div>

     
    </section>
  );
}

